// FieldVision Frontend API Configuration
// Environment-based API URL configuration for dev/staging/production
// FIX: Tilfoej download-file endpoint til faktisk fil-download
// FIX: getZoneMapsByField nu med optional CVR parameter (2025-12-30)
// FIX: getZoneMapsBatchCounts nu med optional CVR parameter (2025-12-30)
// NEW 2025-12-31: getDownloadFileUrl nu med terminal parameter for Trimble support
// NEW 2026-01-13: Tilfojet fetchFieldsWithLogs og fetchDeletedLogs

/**
 * API Base URL Configuration
 *
 * Uses environment variable REACT_APP_API_URL if available,
 * otherwise falls back to localhost for development.
 *
 * Environments:
 * - Development: http://localhost:4002 (local backend)
 * - Staging: http://localhost:4002 (staging backend)
 * - Production: https://fieldvision-api.politemushroom-acd865dd.northeurope.azurecontainerapps.io
 *
 * Set via .env files:
 * - .env.development
 * - .env.production
 * - .env.local (for local overrides, not committed)
 */
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4002';

// Validate API URL format
if (!API_URL) {
  console.error('API_URL is not configured');
}

/**
 * AuthResponse Type
 * Response structure from login/register endpoints
 */
export interface AuthResponse {
  userId: number;
  token: string;
  email: string;
  name?: string;
  cvr?: string;
}

/**
 * UserProfile Type
 * Response structure from profile endpoints
 */
export interface UserProfile {
  userId: number;
  email: string;
  name: string;
  phone: string;
  companyName: string;
  address: string;
  postalCode: string;
  city: string;
  cvr: string;
  createdAt?: string;
  lastLogin?: string;
}

/**
 * NEW: FieldWithLogs Type
 * Response structure for fields that have application logs
 */
export interface FieldWithLogs {
  cvr: string;
  marknr: string;
  areal: number;
  logCount: number;
  geometry: any;
}

/**
 * NEW: DeletedLog Type
 * Response structure for deleted application logs
 */
export interface DeletedLog {
  Id: number;
  CVR: string;
  Marknr: string;
  ApplicationDate: string;
  ProductType: string;
  TotalArea: number;
  DeletedAt: string;
}

/**
 * API Endpoints
 * All endpoints are constructed relative to API_URL
 */
export const API_ENDPOINTS = {
  // Authentication
  login: `${API_URL}/api/auth/login`,
  register: `${API_URL}/api/auth/register`,
  requestPasswordReset: `${API_URL}/api/auth/reset-password/request`,
  confirmPasswordReset: `${API_URL}/api/auth/reset-password/confirm`,

  // User Management
  deleteUser: `${API_URL}/api/admin/users/delete`,
  getUserCVRs: `${API_URL}/api/user/cvrs`,
  addUserCVR: `${API_URL}/api/user/cvrs`,
  deleteUserCVR: `${API_URL}/api/user/cvr`, // Base URL - CVR appended as path param
  getUserProfile: `${API_URL}/api/user/profile`,
  updateUserProfile: `${API_URL}/api/user/profile`,

  // Field Operations
  getFields: `${API_URL}/api/fields`,
  searchFields: `${API_URL}/api/fields/search`,
  getFieldsByBounds: `${API_URL}/api/fields/by-bounds`,

  // Order Management
  createOrder: `${API_URL}/api/orders`,
  getOrders: `${API_URL}/api/orders`,

  // ZoneMaps endpoints
  getZoneMaps: `${API_URL}/api/zonemaps/user`,
  getZoneMapsByField: (fieldId: string) => `${API_URL}/api/zonemaps/field/${fieldId}`,
  getZoneMapsByOrder: (orderId: number) => `${API_URL}/api/zonemaps/order/${orderId}`,
  getZoneMapsBatchCounts: `${API_URL}/api/zonemaps/batch-counts`,
  getZoneMap: (id: number) => `${API_URL}/api/zonemaps/${id}`,
  downloadZoneMap: (id: number) => `${API_URL}/api/zonemaps/${id}/download`,
  // FIX: Nyt endpoint til faktisk fil-download med omdoebte filer
  downloadZoneMapFile: (id: number) => `${API_URL}/api/zonemaps/${id}/download-file`,

  // NEW: Application Logs endpoints
  getFieldsWithLogs: `${API_URL}/api/application-logs/fields-with-logs`,
  getDeletedLogs: `${API_URL}/api/application-logs/deleted`,
  restoreLog: (logId: number) => `${API_URL}/api/application-logs/${logId}/restore`,
} as const;

/**
 * API Configuration
 */
export const API_CONFIG = {
  baseURL: API_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
} as const;

/**
 * Environment information
 */
export const ENV_INFO = {
  apiUrl: API_URL,
  environment: process.env.NODE_ENV || 'development',
  isProduction: process.env.NODE_ENV === 'production',
  isDevelopment: process.env.NODE_ENV === 'development',
} as const;

// Log configuration in development
if (ENV_INFO.isDevelopment) {
  console.log('API Configuration:', {
    baseURL: API_URL,
    environment: ENV_INFO.environment,
  });
}

/**
 * Helper function to construct endpoint URLs with query parameters
 */
export function buildURL(endpoint: string, params?: Record<string, string | number | boolean>): string {
  if (!params) return endpoint;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `${endpoint}?${queryString}` : endpoint;
}

/**
 * Type-safe API request wrapper
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const token = localStorage.getItem('token');

  const headers = {
    ...API_CONFIG.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options?.headers,
  };

  try {
    const response = await fetch(endpoint, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(error.error || `Request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      console.error('API Request Error:', error.message);
      throw error;
    }
    throw new Error('Unknown API error occurred');
  }
}

/**
 * API Service Object
 * Contains all API methods for frontend consumption
 */
const apiService = {
  // Authentication methods
  async login(credentials: { email: string; password: string }): Promise<AuthResponse> {
    const response = await apiRequest<AuthResponse>(API_ENDPOINTS.login, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    // Store token and user info
    if (response.token) {
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response));
    }

    return response;
  },

  async register(data: { email: string; password: string; name?: string; cvr?: string }): Promise<AuthResponse> {
    const response = await apiRequest<AuthResponse>(API_ENDPOINTS.register, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    // Store token and user info
    if (response.token) {
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response));
    }

    return response;
  },

  // Get currently logged in user
  getCurrentUser(): AuthResponse | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    try {
      return JSON.parse(userStr) as AuthResponse;
    } catch (err) {
      console.error('Failed to parse user data:', err);
      return null;
    }
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return !!(token && user);
  },

  // Logout user
  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // User Profile
  async getUserProfile(): Promise<UserProfile> {
    return apiRequest<UserProfile>(API_ENDPOINTS.getUserProfile, {
      method: 'GET',
    });
  },

  async updateUserProfile(data: {
    name?: string;
    phone?: string;
    companyName?: string;
    address?: string;
    postalCode?: string;
    city?: string;
    cvr?: string;
    password?: string;
    confirmPassword?: string;
  }): Promise<{ message: string; profile: UserProfile }> {
    return apiRequest(API_ENDPOINTS.updateUserProfile, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // User CVR Management
  async getUserCVRs() {
    return apiRequest(API_ENDPOINTS.getUserCVRs, {
      method: 'GET',
    });
  },

  async addUserCVR(cvr: string) {
    return apiRequest(API_ENDPOINTS.addUserCVR, {
      method: 'POST',
      body: JSON.stringify({ cvr }),
    });
  },

  async deleteUserCVR(cvr: string) {
    // Backend expects: DELETE /api/user/cvr/:cvr
    const url = `${API_ENDPOINTS.deleteUserCVR}/${encodeURIComponent(cvr)}`;
    return apiRequest(url, {
      method: 'DELETE',
    });
  },

  // Field Operations
  async getFields(cvr?: string) {
    const url = cvr ? buildURL(API_ENDPOINTS.getFields, { cvr }) : API_ENDPOINTS.getFields;
    return apiRequest(url, {
      method: 'GET',
    });
  },

  async searchFields(cvrs: string[]) {
    // Convert array to comma-separated string for query parameter
    const cvrsParam = cvrs.join(',');
    return apiRequest(buildURL(API_ENDPOINTS.searchFields, { cvr: cvrsParam }), {
      method: 'GET',
    });
  },

  async getFieldsByBounds(bounds: { north: number; south: number; east: number; west: number }) {
    const url = `${API_ENDPOINTS.getFieldsByBounds}?north=${bounds.north}&south=${bounds.south}&east=${bounds.east}&west=${bounds.west}`;
    return apiRequest(url, {
      method: 'GET',
    });
  },

  // Order Management
  async createOrder(orderData: any) {
    return apiRequest(API_ENDPOINTS.createOrder, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  async getOrders() {
    return apiRequest(API_ENDPOINTS.getOrders, {
      method: 'GET',
    });
  },

  // ZoneMaps functions
  async getZoneMaps() {
    return apiRequest(API_ENDPOINTS.getZoneMaps, {
      method: 'GET',
    });
  },

  /**
   * FIX: getZoneMapsByField nu med optional CVR parameter
   * Hvis CVR er angivet, returneres kun kort fra det specifikke CVR
   * Dette forhindrer at kort fra andre CVR'er med samme Marknr vises
   *
   * @param fieldId - Marknr (f.eks. "8-0")
   * @param cvr - Optional: CVR nummer til at filtrere kort
   */
  async getZoneMapsByField(fieldId: string, cvr?: string) {
    let url = API_ENDPOINTS.getZoneMapsByField(fieldId);
    if (cvr) {
      url = buildURL(url, { cvr });
    }
    return apiRequest(url, {
      method: 'GET',
    });
  },

  // FIX: Ny funktion til at hente kort via OrderId
  async getZoneMapsByOrder(orderId: number) {
    return apiRequest(API_ENDPOINTS.getZoneMapsByOrder(orderId), {
      method: 'GET',
    });
  },

  /**
   * FIX: getZoneMapsBatchCounts nu med optional CVR parameter
   * Hvis CVR er angivet, returneres kun kort-antal fra det specifikke CVR
   * Dette forhindrer at kort fra andre CVR'er med samme Marknr taelles med
   *
   * @param marknrs - Array af Marknr (f.eks. ["8-0", "10-0"])
   * @param cvr - Optional: CVR nummer til at filtrere kort
   */
  async getZoneMapsBatchCounts(marknrs: string[], cvr?: string): Promise<Record<string, { totalMaps: number; hasNDVI: boolean; hasUkrudt: boolean }>> {
    const body: { marknrs: string[]; cvr?: string } = { marknrs };
    if (cvr) {
      body.cvr = cvr;
    }
    return apiRequest(API_ENDPOINTS.getZoneMapsBatchCounts, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async getZoneMap(id: number) {
    return apiRequest(API_ENDPOINTS.getZoneMap(id), {
      method: 'GET',
    });
  },

  async downloadZoneMap(id: number, data: { terminal: string; unit: string; zones: any }) {
    return apiRequest(API_ENDPOINTS.downloadZoneMap(id), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * NEW 2025-12-31: Hent download URL med authentication token og terminal parameter
   * Terminal parameter styrer hvilket format der returneres:
   * - john-deere: RX.zip med standard John Deere shapefile format
   * - trimble: <marknr>_<type>_<dato>.zip med Trimble AgGPS/AgData folder struktur
   *
   * @param id - ZoneMap ID
   * @param terminal - Terminal type: 'john-deere' eller 'trimble' (default: 'john-deere')
   */
  getDownloadFileUrl(id: number, terminal: string = 'john-deere'): string {
    const token = localStorage.getItem('token');
    return `${API_ENDPOINTS.downloadZoneMapFile(id)}?token=${encodeURIComponent(token || '')}&terminal=${encodeURIComponent(terminal)}`;
  },

  // ======================================
  // NEW 2026-01-13: Application Logs API
  // ======================================

  /**
   * Fetch all fields that have application logs
   * Returns only fields belonging to the user's CVRs
   */
  async fetchFieldsWithLogs(): Promise<{ fields: FieldWithLogs[]; totalCount: number }> {
    return apiRequest(API_ENDPOINTS.getFieldsWithLogs, {
      method: 'GET',
    });
  },

  /**
   * Fetch deleted application logs for the user
   * Returns logs that have IsDeleted = 1
   */
  async fetchDeletedLogs(): Promise<{ logs: DeletedLog[]; count: number }> {
    return apiRequest(API_ENDPOINTS.getDeletedLogs, {
      method: 'GET',
    });
  },

  /**
   * Restore a deleted application log
   * @param logId - The ID of the log to restore
   */
  async restoreLog(logId: number): Promise<{ success: boolean; message: string }> {
    return apiRequest(API_ENDPOINTS.restoreLog(logId), {
      method: 'POST',
    });
  },
};

/**
 * Export apiService as default export
 */
export default apiService;
